// src/components/SOWGenerator.js
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import template from '../assets/templates/SOW-Template.docx';
import {
    MONITORING_SCOPE,
    getProjectTimeline,
    SLA_DETAILS,
    PROJECT_DELIVERABLES,
    getProjectAssumptions,
    PROJECT_EXCLUSIONS,
    CLIENT_RESPONSIBILITIES,
    QUALITY_ASSURANCE,
    getCommunicationPlan,
    getImplementationPhases,
    getSupportProcedures,
    getTermsAndConditions,
    formatSLA
} from '../utils/sowConstants'; // Correct path

export const generateSOW = async (quote, state, regionPricing, instanceTypes, supportTiers) => {
    try {
        const response = await fetch(template);
        const templateContent = await response.arrayBuffer();
        const zip = new PizZip(templateContent);
        const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true
        });

        const getInstanceTypeKeyByName = (instanceTypes, name) => {
            for (const [key, value] of Object.entries(instanceTypes)) {
                if (value.name === name) {
                    return key;
                }
            }
            return null;
        };
        const workloadKey = getInstanceTypeKeyByName(instanceTypes, instanceTypes[state.workload].name);
        const uatWorkloadKey = state.uatServer ? getInstanceTypeKeyByName(instanceTypes, instanceTypes[state.uatWorkload].name) : null;
        const drWorkloadKey = state.drServer ? getInstanceTypeKeyByName(instanceTypes, instanceTypes[state.drWorkload].name) : null;


        const getRegionByName = (regionPricing, name) => {
            for (const [key, value] of Object.entries(regionPricing)) {
                if (value.name === name) {
                    return key;
                }
            }
            return null;
        };
        const aws_region = getRegionByName(regionPricing, regionPricing[state.region].name);

        // Format customer details
        const customerDetails = {
            companyName: state.customerName || "Client Company",
            address: [
                state.streetNumber + " " + state.streetName,
                state.city,
                state.state,
                state.postcode,
                state.country
            ].filter(Boolean).join(", "),
            contactName: `${state.contactFirstName} ${state.contactLastName}`,
            contactTitle: state.contactTitle,
            contactEmail: state.emailAddress,
            contactPhone: state.phoneNumber
        };

        // Check if the email is from paperlesswms.com.au
        const isPaperless = state.emailAddress.toLowerCase().endsWith('paperlesswms.com.au');

        // Adjust support features and monitoring scope based on email
        let adjustedSupportTiers = { ...supportTiers };
        let adjustedSLADetails = { ...SLA_DETAILS };

        if (isPaperless) {
            for (const tierKey in adjustedSupportTiers) {
                adjustedSupportTiers[tierKey] = {
                    ...adjustedSupportTiers[tierKey],
                    features: adjustedSupportTiers[tierKey].features.map(feature => {
                        if (feature.startsWith('Business hours support') || feature.startsWith('Extended support')) {
                            return '24x7 support';
                        }
                        return feature;
                    })
                };
            }
             for (const tierKey in adjustedSLADetails) {
                adjustedSLADetails[tierKey] = {
                  ...adjustedSLADetails[tierKey],
                  monitoring: "24x7" // All tiers are 24x7
                };
             }
        }

        // Use adjustedSupportTiers for features and monitoring scope
        const supportFeatures = adjustedSupportTiers[state.supportTier].features.map(feature =>
            `• ${feature}`
        ).join('\n');

        const monitoringScope = isPaperless ? MONITORING_SCOPE.enterprise.join('\n') : MONITORING_SCOPE[state.supportTier].join('\n');
        const slaDetails = adjustedSLADetails[state.supportTier];


        // Format infrastructure components
        const infrastructureItems = [
            `• AWS Infrastructure hosted in ${regionPricing[state.region].name}`,
            `• ${instanceTypes[state.workload].name} compute environment`,
            `• ${state.s3Storage}GB S3 Storage configuration`,
            `• ${instanceTypes[state.workload].storage}GB Instance Storage`,
            state.uatServer && `• ${instanceTypes[state.uatWorkload].name} UAT environment configuration`,
            state.drServer && `• ${instanceTypes[state.drWorkload].name} Disaster Recovery environment with real-time replication`,
            state.siteCount > 0 && `• Network configuration for ${state.siteCount} site${state.siteCount > 1 ? 's' : ''}`,
            state.centralizedVpn && "• Centralized VPN Hub architecture",
            state.requiresNlb && "• Network Load Balancer configuration",
            state.requiresAlb && "• Application Load Balancer with Cognito and CloudFront integration"
        ].filter(Boolean).join('\n');

        // Format environment details
        const environmentDetails = [
            `• Primary Production Environment in ${regionPricing[state.region].name}`,
            state.uatServer && `• User Acceptance Testing (UAT) Environment`,
            state.drServer && `• Disaster Recovery Environment with near real-time replication`,
            `• Network Configuration for ${state.siteCount} connected site${state.siteCount !== 1 ? 's' : ''}`,
            state.centralizedVpn && `• Centralized VPN Hub architecture`,
            state.requiresNlb && `• Network Load Balancer for traffic distribution`,
            state.requiresAlb && `• Application Load Balancer with Cognito authentication and CloudFront CDN`
        ].filter(Boolean).join('\n');

        // Format project timeline
        const projectTimeline = getProjectTimeline(state);

        // Create pricing items array from infrastructure costs
        const pricingItems = [];

        // Add environment costs
        Object.entries(quote.infrastructure.costs.environment).forEach(([key, item]) => {
            if (item.cost > 0) {
                pricingItems.push({
                    description: item.description,
                    amount: item.cost.toFixed(2)
                });
            }
        });

        // Add network costs
        Object.entries(quote.infrastructure.costs.network).forEach(([key, item]) => {
            if (item.cost > 0) {
                pricingItems.push({
                    description: item.description,
                    amount: item.cost.toFixed(2)
                });
            }
        });

        // Add storage costs
        Object.entries(quote.infrastructure.costs.storage).forEach(([key, item]) => {
            if (item.cost > 0) {
                pricingItems.push({
                    description: item.description,
                    amount: item.cost.toFixed(2)
                });
            }
        });

        // Add support & management fee
        pricingItems.push({
            description: `${quote.support.tierDetails.name} Support & Management`,
            amount: quote.support.total.toFixed(2)
        });

        // Add discount if applicable
        if (quote.summary.discountAmount > 0) {
            pricingItems.push({
                description: `Contract Discount (${quote.summary.discountPercentage}%)`,
                amount: `-${quote.summary.discountAmount.toFixed(2)}`
            });
        }

        // Set TOTAL_AMOUNT separately and format it
        const TOTAL_AMOUNT = quote.summary.finalTotal;


        doc.setData({
            INSTANCE_TYPE: workloadKey,
            UAT_INSTANCE_TYPE: uatWorkloadKey,
            DR_INSTANCE_TYPE: drWorkloadKey,
            STORAGE_DETAILS: `${state.instanceStorage}GB`,
            NETWORK_DETAILS: `${state.siteCount} VPN connection(s) to remote site(s)`,
            REGION: aws_region,
            PRICING_ITEMS: pricingItems,
            TOTAL_AMOUNT: TOTAL_AMOUNT,
            SUPPORT_TIER_NAME: quote.support.tierDetails.name,
            SLA: formatSLA(state.supportTier, slaDetails, isPaperless),
            DOCUMENT_ID: `SOW-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`,
            CREATION_DATE: new Date().toLocaleDateString(),
            VERSION: "1.0",
            STATUS: "Draft",
            CLIENT_NAME: customerDetails.companyName,
            CLIENT_ADDRESS: customerDetails.address,
            CONTACT_NAME: customerDetails.contactName,
            CONTACT_TITLE: customerDetails.contactTitle,
            CONTACT_EMAIL: customerDetails.contactEmail,
            CONTACT_PHONE: customerDetails.contactPhone,
            PROJECT_TITLE: `AWS Implementation for ${customerDetails.companyName}`,
            INFRASTRUCTURE_SCOPE: infrastructureItems,
            ENVIRONMENT_DETAILS: environmentDetails,
            SUPPORT_FEATURES: supportFeatures,
            MONITORING_SCOPE: monitoringScope,
            RESPONSE_TIME: slaDetails.responseTime,
            AVAILABILITY: slaDetails.availability,
            MONITORING_HOURS: slaDetails.monitoring,
            PROJECT_TIMELINE: projectTimeline,
            MONTHLY_INFRASTRUCTURE: quote.infrastructure.total.toFixed(2),
            MONTHLY_SUPPORT: quote.support.total.toFixed(2),
            CONTRACT_LENGTH: state.contractLength,
            DISCOUNT_PERCENTAGE: quote.summary.discountPercentage,
            MONTHLY_TOTAL: quote.summary.finalTotal,
            TOTAL_CONTRACT_VALUE: (parseFloat(quote.summary.finalTotal) * state.contractLength).toFixed(2),
            // IMPLEMENTATION_FEE: state.implementationFee.toFixed(2), // Remove Implementation Fee
            PROJECT_DELIVERABLES: PROJECT_DELIVERABLES.join('\n'),
            PROJECT_ASSUMPTIONS: getProjectAssumptions(state).join('\n'),
            PROJECT_EXCLUSIONS: PROJECT_EXCLUSIONS.join('\n'),
            CLIENT_RESPONSIBILITIES: CLIENT_RESPONSIBILITIES.join('\n'),
            QUALITY_ASSURANCE: QUALITY_ASSURANCE.filter(item => item !== false).join('\n'),
            COMMUNICATION_PLAN: getCommunicationPlan(state, adjustedSupportTiers),
            IMPLEMENTATION_PHASES: getImplementationPhases(state),
            SUPPORT_PROCEDURES: getSupportProcedures(state, slaDetails).join('\n'),
            TERMS_AND_CONDITIONS: getTermsAndConditions(state, slaDetails).join('\n')
        });

        doc.render();

        return doc.getZip().generate({
            type: 'blob',
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

    } catch (error) {
        console.error('Error generating document:', error);
        if (error.properties && error.properties.errors instanceof Array) {
            console.log('Error details:', error.properties.errors);
        }
        throw error;
    }
};
